import { useState } from 'react';
import { PlanCards, type BillingPeriod } from '@/features/pricing/PlanCards';
import { Icon } from '@/components/Icon';
import { CURRENCIES, annualPrice, formatPrice, monthlyPrice, type CurrencyCode } from '@/lib/pricing';
import { annualSavingPct } from '@/lib/pricing';
import type { Tier } from '@/lib/tier';
import './Onboarding.css';

/**
 * Onboarding screen 2 — plan choice.
 *
 * Shown after the Kinde account exists and before checkout. Identity first, then billing: an
 * account can exist without a subscription, and a customer who abandons here must be
 * recoverable — send them back to this screen on next login rather than orphaning the account
 * or making them sign up twice.
 *
 * Uses the same `PlanCards` as in-app Pricing, so the two can never disagree.
 *
 * Two things this screen adds on top of the cards:
 *   - a strip confirming the account just created, so the handoff back from Kinde is legible
 *   - a preview of what checkout will show, so the $0.00-today figure is not a surprise
 */
export function PlanChoice({
  accountEmail,
  chargeDate,
  currency = 'USD',
  onChoose,
  busy = false,
  error = null,
}: {
  /** The Kinde email — the account identity. Checkout may collect a different billing email. */
  accountEmail: string;
  chargeDate: string;
  currency?: CurrencyCode;
  /** Receives the period as well as the tier: the two together select exactly one price. */
  onChoose: (tier: Tier, period: BillingPeriod) => void;
  /** Checkout is being created. The cards lock so a second click cannot open a second one. */
  busy?: boolean;
  error?: string | null;
}) {
  const [period, setPeriod] = useState<BillingPeriod>('monthly');
  const [selected, setSelected] = useState<Tier | null>(null);
  const savingPct = annualSavingPct(currency, 'intelligence');

  // The preview must quote the period actually selected. Quoting the monthly figure while the
  // annual toggle is active understates the first charge by an order of magnitude, and this is
  // the screen whose whole job is disclosing that charge before a card is taken.
  const firstCharge = (tier: Tier) =>
    period === 'annual' ? annualPrice(currency, tier) : monthlyPrice(currency, tier);

  return (
    <div className="choice">
      <div className="choice__inner">
        <header className="choice__head">
          <h1 className="choice__title">Pick your plan</h1>
          <p className="choice__subtitle">
            All plans include a 14-day free trial. Change or cancel whenever you like.
          </p>
        </header>

        {/* Confirms the account that was just created, so returning from Kinde makes sense. */}
        <p className="choice__account">
          <Icon name="check" /> Account created for <strong>{accountEmail}</strong>
        </p>

        <div className="choice__toggle" role="tablist" aria-label="Billing period">
          <button
            type="button"
            role="tab"
            aria-selected={period === 'monthly'}
            data-active={period === 'monthly'}
            onClick={() => setPeriod('monthly')}
          >
            Monthly
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={period === 'annual'}
            data-active={period === 'annual'}
            onClick={() => setPeriod('annual')}
          >
            Pay yearly <span className="choice__save">Save {savingPct}%</span>
          </button>
        </div>

        <PlanCards
          currency={currency}
          period={period}
          onChoose={(tier) => {
            if (busy) return;
            setSelected(tier);
            onChoose(tier, period);
          }}
        />

        {busy && <p className="choice__status">Opening secure checkout…</p>}
        {error && (
          <p className="choice__status choice__status--error" role="alert">
            {error}
          </p>
        )}

        {/* What checkout will show. Stating it here means the trial terms are disclosed in
            both places, which is the whole defense against chargebacks. */}
        <div className="choice__preview">
          <Icon name="calendar" className="choice__previewIcon" />
          <div>
            <p className="choice__previewLead">Checkout will show $0.00 due today.</p>
            <p className="choice__previewBody">
              {selected ? (
                <>
                  Your first charge is{' '}
                  <strong>
                    {formatPrice(currency, firstCharge(selected))} on {chargeDate}
                  </strong>
                  {period === 'annual' ? ', then yearly.' : '.'}
                </>
              ) : (
                <>
                  Your first charge lands on <strong>{chargeDate}</strong>, at whichever plan you
                  pick.
                </>
              )}{' '}
              A card is required to start the trial. Cancel before {chargeDate} and nothing is
              taken.
            </p>
          </div>
        </div>

        <p className="choice__footnote">
          Prices in {CURRENCIES[currency].label}. Top-up packs for Aware Explain and Ask Aware
          are a one-time purchase and never expire.
        </p>
      </div>
    </div>
  );
}
